import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/**
 * DangerZone — the account-nuke button. Wrapped in an AlertDialog with a
 * type-to-confirm input (must be exactly "DELETE") so a stray tap can't wipe
 * a tradie's whole ledger. Backend cross-checks the same phrase before it
 * touches Mongo — belt AND suspenders.
 *
 * On success we hand off to AuthContext.setUser(false) so Protected bounces
 * to /login, then hard-nav so any cached state elsewhere (localStorage,
 * AppContext) is torn down cleanly.
 */
export default function DangerZone() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  const canDelete = phrase.trim() === "DELETE" && !busy;

  const doDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await api.delete("/auth/account", { data: { confirmation: "DELETE" } });
      // Nuke client-side state so nothing lingers.
      try {
        localStorage.removeItem("activeJobId");
      } catch (err) {
        devLog.warn("localStorage clear failed", err);
      }
      setUser(false);
      toast.success("Your account and all data have been deleted.", {
        duration: 5000,
      });
      // Hard nav so AppContext/AuthContext fully re-init on next login.
      nav("/login", { replace: true });
    } catch (e) {
      devLog.warn("account delete failed", e);
      const detail = e.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "We couldn't delete the account. Please try again."
      );
      setBusy(false);
    }
  };

  return (
    <section
      className="bi-surface p-4 space-y-3 border border-[#F87171]/25"
      data-testid="danger-zone"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#F87171]/15 text-[#F87171] flex items-center justify-center shrink-0">
          <AlertTriangle className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-sm text-white">
            Delete my account &amp; all data
          </div>
          <p className="text-[12px] text-white/60 mt-1 leading-snug">
            Permanently removes your account, every receipt, job, category,
            invoice, photo and message. This can&apos;t be undone.
          </p>
        </div>
      </div>

      <AlertDialog
        open={open}
        onOpenChange={(v) => {
          if (!v) setPhrase("");
          setOpen(v);
        }}
      >
        <AlertDialogTrigger asChild>
          <button
            type="button"
            data-testid="delete-account-open"
            className="w-full h-11 rounded-xl border border-[#F87171]/40 text-[#F87171] text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 hover:bg-[#F87171]/10 transition"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete account &amp; all data
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent
          data-testid="delete-account-dialog"
          className="bg-[#1F1F1F] border-white/10 text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#F87171]" />
              Delete your account?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/70 space-y-2">
              <span className="block">
                This wipes every receipt, job, category, invoice, photo and
                message tied to your account. It happens immediately and
                can&apos;t be recovered — not by us, not by support.
              </span>
              <span className="block text-white/85">
                To confirm, type{" "}
                <b className="text-[#F87171] tracking-wide">DELETE</b> below.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <input
            data-testid="delete-account-input"
            type="text"
            autoComplete="off"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder="Type DELETE to confirm"
            className="w-full bi-input tracking-widest"
            disabled={busy}
          />

          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-account-cancel"
              disabled={busy}
              className="bi-ghost-btn border-white/10 text-white/80"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-account-confirm"
              onClick={(e) => {
                // AlertDialog closes on default click. Prevent so we can await
                // the API + toast before the dialog unmounts.
                e.preventDefault();
                doDelete();
              }}
              disabled={!canDelete}
              className="bg-[#F87171] hover:bg-[#F87171]/90 text-[#1F1F1F] font-heading font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" /> Delete forever
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
